import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import type { DeliveryMapResponse } from './deliveries-types'
import { api } from '@/api/api'

/**
 * The Routific stop for one order, read off that day's delivery map. Routific cleans up
 * the address EBMS pushes, so the packing list prints its version. Read-only: `null` when
 * Routific has no stop for the order (not pushed yet, or the invoice didn't match).
 */
export const useOrderDeliveryStop = (autoid: string | undefined, shipDate: string | null | undefined) => {
  // Stable, so React Query re-runs the lookup only when the map or the order changes.
  const select = useCallback(
    (map: DeliveryMapResponse) => map.stops.find((stop) => stop.order_autoid === autoid) ?? null,
    [autoid]
  )

  return useQuery({
    queryKey: ['deliveries-map', shipDate],
    queryFn: async () => {
      const { data } = await api.get<DeliveryMapResponse>('/deliveries/map/', { params: { date: shipDate } })
      return data
    },
    enabled: !!autoid && !!shipDate,
    // One address off a whole day's map — no need to pull it again on every focus
    // (printing alone hands focus back) or every time a sheet remounts.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    select
  })
}
